// Glossary terms in running text ([[term]] in content, a.glossary-term) open a popup with the term's
// glossary entry, from the hidden #glossary-definitions the layout adds (shared/_glossary_definitions).
// - Mouse: hover (or keyboard focus) opens it; it stays open while the pointer is over the term or the
//   popup; clicking the term goes to the glossary, as the plain link always did.
// - Touch: the first tap opens it instead of following the link; a second tap on the same term, or the
//   popup's "Open in Glossary" link, goes to the glossary; a tap anywhere else closes it.
// Without JavaScript the term stays a link to the glossary with the short definition as its title.
import { computePosition, autoUpdate, offset, flip, shift, inline, size } from "@floating-ui/dom";

const OPEN_DELAY = 120;
const CLOSE_DELAY = 150; // time to move the pointer from the term into the popup
const EDGE = 12; // px kept clear of the viewport's edges

export default function initGlossaryPopovers() {
	const source = document.getElementById("glossary-definitions");
	if (!source) return;
	const definitions = new Map([...source.querySelectorAll("[data-glossary-definition]")].map((el) => [el.dataset.glossaryDefinition, el]));
	const triggers = [...document.querySelectorAll("a[data-glossary-term]")].filter((a) => definitions.has(a.dataset.glossaryTerm));
	if (!triggers.length) return;

	const canHover = window.matchMedia("(hover: hover) and (pointer: fine)");
	const popover = document.createElement("div");
	popover.id = "glossary-popover";
	popover.className = "glossary-popover";
	popover.setAttribute("role", "tooltip");
	popover.hidden = true;
	document.body.appendChild(popover);

	let current = null, stopUpdating = null, openTimer = null, closeTimer = null;

	const place = () => computePosition(current, popover, {
		placement: "top",
		middleware: [
			inline(), // a term that wraps onto two lines anchors to the line the pointer is on
			offset(8),
			flip({ padding: EDGE }),
			shift({ padding: EDGE }),
			size({ padding: EDGE, apply: ({ availableHeight }) => { popover.style.maxHeight = `${Math.max(160, Math.min(availableHeight, 480))}px`; } })
		]
	}).then(({ x, y }) => Object.assign(popover.style, { left: `${x}px`, top: `${y}px` }));

	function open(trigger) {
		clearTimeout(closeTimer);
		if (current === trigger) return;
		if (current) close();
		current = trigger;
		popover.replaceChildren(definitions.get(trigger.dataset.glossaryTerm).cloneNode(true));
		popover.hidden = false;
		popover.scrollTop = 0;
		trigger.setAttribute("aria-describedby", popover.id);
		trigger.setAttribute("aria-expanded", "true");
		stopUpdating = autoUpdate(trigger, popover, place);
	}

	function close() {
		clearTimeout(openTimer);
		if (!current) return;
		stopUpdating?.();
		current.removeAttribute("aria-describedby");
		current.setAttribute("aria-expanded", "false");
		popover.hidden = true;
		current = null;
	}

	const closeSoon = () => { clearTimeout(openTimer); closeTimer = setTimeout(close, CLOSE_DELAY); };

	triggers.forEach((trigger) => {
		trigger.removeAttribute("title"); // the popup replaces the browser's tooltip
		trigger.setAttribute("aria-expanded", "false");

		trigger.addEventListener("pointerenter", (e) => {
			if (e.pointerType !== "mouse" || !canHover.matches) return;
			clearTimeout(closeTimer);
			openTimer = setTimeout(() => open(trigger), current ? 0 : OPEN_DELAY);
		});
		trigger.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") closeSoon(); });
		trigger.addEventListener("focus", () => { if (trigger.matches(":focus-visible")) open(trigger); });
		trigger.addEventListener("blur", () => { if (current === trigger) close(); });
		trigger.addEventListener("click", (e) => {
			if (canHover.matches) return; // with a mouse, clicking the term goes to the glossary
			if (current !== trigger) { e.preventDefault(); open(trigger); }
		});
	});

	popover.addEventListener("pointerenter", () => clearTimeout(closeTimer));
	popover.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") closeSoon(); });
	document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
	document.addEventListener("click", (e) => {
		if (current && !popover.contains(e.target) && !current.contains(e.target)) close();
	});
}
